import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Skeleton,
  Button,
  Collapse,
  Tooltip
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { motion, AnimatePresence } from 'framer-motion';

import { CustomBreadcrumbs, CustomPagination, PageBanner, ExamTypeChip, CustomTableContainer, CustomTable, CustomTableHead, CustomTableRow, CustomTableCell, CustomTableBody } from '../../common';
import teacherBannerBg from '../../../assets/images/teacher.jpg';
import examService from '../../../infrastructure/services/examService';
import teacherService from '../../../infrastructure/services/teacherService';
import { formatSriLankanDate } from '../../../infrastructure/services/timeSyncService';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, '');
};

const getGradeFromScore = (scorePercent) => {
  const score = Number(scorePercent) || 0;
  if (score >= 75) return 'A';
  if (score >= 65) return 'B';
  if (score >= 50) return 'C';
  if (score >= 35) return 'S';
  return 'F';
};

const makeResultsForScore = (scorePercent) => ({
  answers: {},
  questions: [],
  totalCount: 0,
  correctCount: 0,
  wrongCount: 0,
  skippedCount: 0,
  scorePercentage: scorePercent
});

const getProfileImageUrl = (pic) => {
  if (!pic) return null;
  if (pic.startsWith('http://') || pic.startsWith('https://')) {
    return pic;
  }
  return `https://fonixedugrading.blob.core.windows.net/eduone-auth-content/${pic}`;
};

const getInitials = (name) => {
  if (!name) return 'T';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Shared chip styling handled by common component ExamTypeChip

const getGradeChip = (grade) => {
  const normalized = (grade || '').toUpperCase();
  let color = '#64748b';
  let bgcolor = '#f1f5f9';

  if (normalized.startsWith('A')) {
    color = '#10b981';
    bgcolor = '#ecfdf5';
  } else if (normalized.startsWith('B')) {
    color = '#f59e0b';
    bgcolor = '#fffbeb';
  } else if (normalized.startsWith('C')) {
    color = '#3b82f6';
    bgcolor = '#eff6ff';
  } else if (normalized.startsWith('S')) {
    color = '#8b5cf6';
    bgcolor = '#f3e8ff';
  } else if (normalized.startsWith('F')) {
    color = '#ef4444';
    bgcolor = '#fef2f2';
  }

  return (
    <Box
      sx={{
        bgcolor,
        color,
        fontWeight: 800,
        fontFamily: 'Poppins',
        borderRadius: '8px',
        fontSize: '13.5px',
        height: '28px',
        width: '28px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1.5px solid ${color}20`
      }}
    >
      {grade}
    </Box>
  );
};

const formatDuration = (exam) => {
  const hours = Number(exam.durationHours) || 0;
  const minutes = Number(exam.durationMinutes) || 0;
  if (!hours && !minutes) return 'N/A';
  return `${hours}H ${minutes}M`;
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return formatSriLankanDate(date);
  } catch (e) {
    return 'N/A';
  }
};


export default function YourResultPage() {
  const navigate = useNavigate();
  const { teacherId } = useParams();

  // Navigation and Selection States
  const [view, setView] = useState('teachers'); // 'teachers' | 'results'
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // Teachers State
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showSubjectFilters, setShowSubjectFilters] = useState(false);
  const [teacherPage, setTeacherPage] = useState(1);

  // Graded Exams State
  const [exams, setExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const [examSearch, setExamSearch] = useState('');
  const [examPage, setExamPage] = useState(1);
  const [examsTotalPages, setExamsTotalPages] = useState(1);
  const [examsTotalItems, setExamsTotalItems] = useState(0);

  // Gathers all unique subjects from the teachers list for the filter options
  const allUniqueSubjects = Array.from(new Set(teachers.flatMap((t) => t.subjects || [])));

  // Premium background colors for avatars based on teacher id hashing
  const avatarGradients = [
    'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)', // Pink / Coral
    'linear-gradient(135deg, #4E65FF 0%, #92EFFD 100%)', // Blue / Teal
    'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', // Emerald Green
    'linear-gradient(135deg, #7F00FF 0%, #E100FF 100%)', // Deep Purple / Violet
    'linear-gradient(135deg, #f12711 0%, #f5af19 100%)', // Sunfire Red / Orange
    'linear-gradient(135deg, #3a7bd5 0%, #3a6073 100%)'  // Steel Grey
  ];

  // 1. Fetch Enrolled Teachers on mount
  useEffect(() => {
    const loadAllTeachers = async () => {
      setTeachersLoading(true);
      try {
        const data = await teacherService.getTeachers({
          search: '',
          sectionId: 'exam',
          page: 1,
          pageSize: 100 // Load all of the student's teachers
        });
        if (data && data.isSuccessful) {
          setTeachers(data.listContent || []);
        }
      } catch (err) {
        console.error('Failed to load teachers:', err);
      } finally {
        setTeachersLoading(false);
      }
    };
    loadAllTeachers();
  }, []);

  // 2. Synchronize view state with route parameters
  useEffect(() => {
    if (teacherId) {
      setView('results');
      const found = teachers.find((t) => t.teacherId === teacherId);
      if (found) {
        setSelectedTeacher(found);
      } else if (teachers.length > 0) {
        // Fallback if not found in list but list is loaded
        setSelectedTeacher({
          teacherId,
          teacherName: 'Teacher',
          subjects: []
        });
      }
      setExamPage(1);
    } else {
      setView('teachers');
      setSelectedTeacher(null);
      setExamPage(1);
    }
  }, [teacherId, teachers]);

  // 3. Fetch Graded Results (Exams) for Selected Teacher
  const fetchGradedResults = async (tId, page) => {
    setExamsLoading(true);
    try {
      const data = await examService.getAssignedExams({
        teacherId: tId,
        status: 'graded',
        sortBy: 'newest',
        page,
        pageSize: 10
      });
      if (data) {
        setExams(data.listContent || []);
        setExamsTotalPages(data.paginationInfo?.totalPages || 1);
        setExamsTotalItems(data.paginationInfo?.totalItems || 0);
      }
    } catch (err) {
      console.error('Failed to fetch student graded results:', err);
      setExams([]);
      setExamsTotalPages(1);
      setExamsTotalItems(0);
    } finally {
      setExamsLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'results' && teacherId) {
      fetchGradedResults(teacherId, examPage);
    }
  }, [examPage, teacherId, view]);

  // Reset teacher page to 1 when filters change
  useEffect(() => {
    setTeacherPage(1);
  }, [teacherSearch, selectedSubject]);

  // Reset exam page to 1 when exam search changes
  useEffect(() => {
    setExamPage(1);
  }, [examSearch]);

  // Filter teachers by search and subject (Client-side for absolute responsiveness)
  const displayedTeachers = teachers.filter((t) => {
    const search = teacherSearch.toLowerCase();
    const matchesSearch =
      t.teacherName.toLowerCase().includes(search) ||
      (t.subjects || []).some((s) => s.toLowerCase().includes(search)) ||
      t.teacherId.toLowerCase().includes(search);
    const matchesSubject = selectedSubject ? (t.subjects || []).includes(selectedSubject) : true;
    return matchesSearch && matchesSubject;
  });

  const TEACHERS_PAGE_SIZE = 12;
  const teachersTotalPages = Math.ceil(displayedTeachers.length / TEACHERS_PAGE_SIZE);
  const paginatedTeachers = displayedTeachers.slice((teacherPage - 1) * TEACHERS_PAGE_SIZE, teacherPage * TEACHERS_PAGE_SIZE);

  // Filter exams by search query (Client-side)
  const filteredExams = exams.filter((exam) => {
    const search = examSearch.toLowerCase();
    return (
      stripHtml(exam.examTitle).toLowerCase().includes(search) ||
      (exam.classExamId && exam.classExamId.toLowerCase().includes(search)) ||
      (exam.subjectName && exam.subjectName.toLowerCase().includes(search)) ||
      (exam.className && exam.className.toLowerCase().includes(search))
    );
  });

  // Action handlers
  const handleTeacherClick = (teacher) => {
    navigate(`/your-result/${teacher.teacherId}`);
  };

  const handleBackToTeachers = () => {
    navigate('/your-result');
  };

  const handleRowClick = (exam) => {
    const isExamGrading = exam.isGrading || (exam.examType !== 'mcq' && exam.gradingStatus !== 'completed');
    if (isExamGrading) return;

    const scorePercent = exam.percentage !== null && exam.percentage !== undefined ? exam.percentage : (exam.totalScore || 0);
    const mappedExam = {
      id: exam.classExamId,
      name: stripHtml(exam.examTitle),
      subject: exam.subjectName,
      topic: exam.className,
      duration: formatDuration(exam),
      type: exam.examType === 'mcq' ? 'MCQ' : exam.examType === 'essay' ? 'Essay' : 'Structure',
      teacherId: selectedTeacher?.teacherId,
      date: formatDate(exam.gradedAt),
      paperUploadId: exam.paperUploadId
    };

    navigate(`/exam-manager/exam-summary/${exam.classExamId}`, {
      state: {
        exam: mappedExam,
        isCompleted: true,
        results: makeResultsForScore(scorePercent),
        fromRoute: selectedTeacher?.teacherId ? `/your-result/${selectedTeacher.teacherId}` : '/your-result'
      }
    });
  };

  // Breadcrumbs configurations
  const breadcrumbItems = view === 'teachers'
    ? [
      { label: 'Home', href: '/dashboard' },
      { label: 'Your Result' }
    ]
    : [
      { label: 'Home', href: '/dashboard' },
      { label: 'Your Result', onClick: handleBackToTeachers, style: { cursor: 'pointer' } },
      { label: selectedTeacher?.teacherName || 'Results' }
    ];

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {/* Main Banner */}
      <PageBanner
        title="Your Results"
        description="View your  graded exam results and marks"
        backgroundImage={teacherBannerBg}
      >
        {view === 'results' && (
          <Button
            variant="outlined"
            onClick={handleBackToTeachers}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.65)',
              color: '#ffffff',
              borderRadius: '14px',
              px: 3,
              py: 0.8,
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.25s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateX(-2px)'
              }
            }}
          >
            <KeyboardArrowLeftRoundedIcon sx={{ fontSize: '18px' }} /> Back
          </Button>
        )}
      </PageBanner>

      <AnimatePresence mode="wait">
        {view === 'teachers' ? (
          // TEACHERS VIEW (Figma Styled Cards)
          <motion.div
            key="teachers-view"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
          >
            {/* Cards Container Paper */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1.5px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                mt: 4
              }}
            >
              {/* Header and Actions Bar */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 3
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#262626', fontFamily: 'Poppins' }}>
                      Your Teachers
                    </Typography>
                    <Chip
                      label={`${displayedTeachers.length} Teachers`}
                      size="small"
                      sx={{
                        bgcolor: '#ECFDF5',
                        color: '#10B981',
                        fontWeight: 500,
                        fontSize: '12px',
                        fontFamily: 'Poppins',
                        borderRadius: '16px',
                        height: '22px'
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'Poppins', mt: 0.5 }}>
                    Select a Teacher to view graded results and completed marks
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap'
                  }}
                >
                  <TextField
                    placeholder="Search"
                    size="small"
                    value={teacherSearch}
                    onChange={(e) => setTeacherSearch(e.target.value)}
                    sx={{
                      width: { xs: '100%', sm: 260 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontFamily: 'Poppins',
                        fontSize: '13px',
                        bgcolor: '#ffffff',
                        '& fieldset': { borderColor: '#cbd5e1' },
                        '&:hover fieldset': { borderColor: '#94a3b8' },
                        '&.Mui-focused fieldset': { borderColor: '#64748b' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                        </InputAdornment>
                      )
                    }}
                  />

                  <Button
                    variant="outlined"
                    onClick={() => setShowSubjectFilters(!showSubjectFilters)}
                    startIcon={
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <FilterListRoundedIcon />
                        {selectedSubject && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -2,
                              right: -2,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#FF502D',
                              border: '1.5px solid #ffffff'
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      borderColor: (showSubjectFilters || selectedSubject) ? '#FF502D' : '#cbd5e1',
                      color: (showSubjectFilters || selectedSubject) ? '#FF502D' : '#64748b',
                      bgcolor: selectedSubject ? 'rgba(255, 80, 45, 0.03)' : 'transparent',
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      fontSize: '13px',
                      height: '40px',
                      '&:hover': {
                        borderColor: '#FF502D',
                        color: '#FF502D',
                        bgcolor: 'rgba(255, 80, 45, 0.08)'
                      }
                    }}
                  >
                    Filters
                  </Button>
                </Box>
              </Box>

              {/* Collapsible Subject Filter Panel */}
              <Collapse in={showSubjectFilters}>
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: '#FFF5F3',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 80, 45, 0.15)'
                  }}
                >
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#FF502D', mb: 1, fontFamily: 'Poppins' }}>
                    Filter by Subject:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label="All Subjects"
                      onClick={() => setSelectedSubject('')}
                      sx={{
                        bgcolor: !selectedSubject ? '#FF502D' : '#ffffff',
                        color: !selectedSubject ? '#ffffff' : '#64748b',
                        border: '1px solid',
                        borderColor: !selectedSubject ? '#FF502D' : '#e2e8f0',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: !selectedSubject ? '#e64525' : '#f8fafc' }
                      }}
                    />
                    {allUniqueSubjects.map((subj) => (
                      <Chip
                        key={subj}
                        label={subj}
                        onClick={() => setSelectedSubject(subj)}
                        sx={{
                          bgcolor: selectedSubject === subj ? '#FF502D' : '#ffffff',
                          color: selectedSubject === subj ? '#ffffff' : '#64748b',
                          border: '1px solid',
                          borderColor: selectedSubject === subj ? '#FF502D' : '#e2e8f0',
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: selectedSubject === subj ? '#e64525' : '#f8fafc' }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Collapse>

              {/* Teachers Card Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, width: '100%' }}>
                {teachersLoading ? (
                  Array.from(new Array(6)).map((_, idx) => (
                    <Box sx={{ display: 'flex', minWidth: 0 }} key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          border: '1.5px solid #e2e8f0',
                          borderRadius: '24px',
                          display: 'flex',
                          height: '120px',
                          width: '100%',
                          overflow: 'hidden'
                        }}
                      >
                        <Skeleton variant="rectangular" width={80} height="100%" />
                        <Box sx={{ flexGrow: 1, p: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                          <Skeleton variant="text" width="60%" height={18} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="50%" height={18} />
                        </Box>
                      </Paper>
                    </Box>
                  ))
                ) : displayedTeachers.length === 0 ? (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 6,
                        textAlign: 'center',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '24px',
                        color: '#94a3b8'
                      }}
                    >
                      No teachers found.
                    </Paper>
                  </Box>
                ) : (
                  paginatedTeachers.map((teacher, index) => {
                    // Hash teacherId to select a gradient
                    const hash = (teacher.teacherId || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                    const gradient = avatarGradients[hash % avatarGradients.length];

                    const primarySubject = teacher.subjects?.[0] || 'General';
                    const classCount = teacher.totalExamCount || teacher.ongoingExamCount || 2;






                    return (
                      <Box sx={{ display: 'flex', minWidth: 0 }} key={teacher.teacherId}>
                        <Paper
                          elevation={0}
                          onClick={() => handleTeacherClick(teacher)}
                          sx={{
                            border: '1.5px solid #e2e8f0',
                            borderRadius: '24px',
                            bgcolor: '#ffffff',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'row',
                            height: '120px',
                            width: '100%',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 24px rgba(255, 80, 45, 0.08)',
                              borderColor: '#FF502D',
                              '& .gradient-overlay': {
                                opacity: 0.15
                              }
                            }
                          }}
                        >
                          {/* Left Column: Image wrapper with gradient and curve fading overlay */}
                          <Box
                            sx={{
                              width: 80,
                              height: '100%',
                              background: gradient,
                              position: 'relative',
                              overflow: 'hidden',
                              flexShrink: 0,
                              borderTopLeftRadius: '22px',
                              borderBottomLeftRadius: '22px'
                            }}
                          >
                            {/* Curved right overlay for the photo layout effect */}
                            <Box
                              className="gradient-overlay"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, rgba(255,255,255,0) 65%, rgba(255,255,255,0.7) 100%)',
                                zIndex: 2,
                                transition: 'opacity 0.3s'
                              }}
                            />
                            {teacher.profilePicture ? (
                              <Box
                                component="img"
                                src={getProfileImageUrl(teacher.profilePicture)}
                                alt={teacher.teacherName}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  imageRendering: '-webkit-optimize-contrast'
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  color: '#ffffff',
                                  fontWeight: 700,
                                  fontSize: '28px',
                                  fontFamily: 'Poppins',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 1
                                }}
                              >
                                {getInitials(teacher.teacherName)}
                              </Typography>
                            )}
                          </Box>

                          {/* Right Column: Info Details */}
                          <Box sx={{ flexGrow: 1, minWidth: 0, px: 1.5, py: 1.25, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <Tooltip title={teacher.teacherName} placement="top" arrow>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '14.5px',
                                  color: '#1e293b',
                                  fontFamily: 'Poppins',
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {(teacher.teacherName || '').length > 25 ? `${(teacher.teacherName || '').substring(0, 25)}...` : (teacher.teacherName || '')}
                              </Typography>
                            </Tooltip>

                            <Stack spacing={0.25} sx={{ mb: 0.5 }}>
                              {/* Subject */}
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                <Box component="span" sx={{ display: 'inline-flex', color: '#94a3b8', flexShrink: 0 }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                                    <line x1="7" y1="7" x2="7.01" y2="7" />
                                  </svg>
                                </Box>
                                <Tooltip title={primarySubject} placement="top" arrow>
                                  <Typography
                                    sx={{
                                      fontSize: '11px',
                                      color: '#64748b',
                                      fontFamily: 'Poppins',
                                      fontWeight: 500,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      width: '100%'
                                    }}
                                  >
                                    {primarySubject}
                                  </Typography>
                                </Tooltip>
                              </Stack>

                              {/* Class Count */}
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                <Box component="span" sx={{ display: 'inline-flex', color: '#94a3b8', flexShrink: 0 }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                  </svg>
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    color: '#64748b',
                                    fontFamily: 'Poppins',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: '100%'
                                  }}
                                >
                                  {classCount} Classes
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* Pagination for Teachers */}
              {!teachersLoading && teachersTotalPages > 1 && (
                <CustomPagination
                  currentPage={Math.min(teacherPage, teachersTotalPages)}
                  totalPages={teachersTotalPages}
                  onPageChange={(page) => setTeacherPage(page)}
                />
              )}
            </Paper>
          </motion.div>
        ) : (
          // GRADED RESULTS TABLE VIEW
          <motion.div
            key="results-view"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
          >
            {/* Table Container Paper */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1.5px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
              }}
            >
              {/* Table Header and Actions Bar */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 3
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#262626', fontFamily: 'Poppins' }}>
                      Graded Results
                    </Typography>
                    <Chip
                      label={`${examsTotalItems} Exams`}
                      size="small"
                      sx={{
                        bgcolor: '#ECFDF5',
                        color: '#10B981',
                        fontWeight: 500,
                        fontSize: '12px',
                        fontFamily: 'Poppins',
                        borderRadius: '16px',
                        height: '22px'
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'Poppins', mt: 0.5 }}>
                    Latest graded exams from this teacher's course
                  </Typography>
                </Box>

                <TextField
                  placeholder="Search Exam"
                  size="small"
                  value={examSearch}
                  onChange={(e) => setExamSearch(e.target.value)}
                  sx={{
                    width: { xs: '100%', sm: 220 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      fontFamily: 'Poppins',
                      fontSize: '13px',
                      bgcolor: '#ffffff',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#64748b' }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              {/* Table List */}
              <CustomTableContainer sx={{ border: 'none', overflowX: 'auto' }}>
                <CustomTable size="medium">
                  <CustomTableHead>
                    <CustomTableRow>
                      {[
                        'Exam Title',
                        'Class and Subject',
                        'Duration Taken',
                        'Completed Date',
                        'Marks',
                        'Grade',
                        'Type'
                      ].map((label) => (
                        <CustomTableCell
                          key={label}
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 600,
                            fontSize: '12px',
                            fontFamily: 'Poppins',
                            borderBottom: '1px solid #f1f5f9',
                            py: 2,
                            bgcolor: '#ffffff'
                          }}
                        >
                          {label}
                        </CustomTableCell>
                      ))}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2, bgcolor: '#ffffff' }} />
                    </CustomTableRow>
                  </CustomTableHead>
                  <CustomTableBody>
                    {examsLoading ? (
                      Array.from(new Array(5)).map((_, index) => (
                        <CustomTableRow key={index}>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="text" width="70%" height={24} sx={{ borderRadius: '4px', mb: 0.5 }} />
                          </CustomTableCell>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: '4px', mb: 0.5 }} />
                            <Skeleton variant="text" width="30%" height={16} sx={{ borderRadius: '4px' }} />
                          </CustomTableCell>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="text" width="40%" height={20} sx={{ borderRadius: '4px' }} />
                          </CustomTableCell>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="text" width="50%" height={20} sx={{ borderRadius: '4px' }} />
                          </CustomTableCell>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="text" width="30%" height={20} sx={{ borderRadius: '4px' }} />
                          </CustomTableCell>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="rectangular" width={40} height={22} sx={{ borderRadius: '6px' }} />
                          </CustomTableCell>
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '8px' }} />
                          </CustomTableCell>
                          <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block' }} />
                          </CustomTableCell>
                        </CustomTableRow>
                      ))
                    ) : filteredExams.length === 0 ? (
                      <CustomTableRow>
                        <CustomTableCell colSpan={8} align="center" sx={{ py: 8, color: '#94a3b8', fontFamily: 'Poppins' }}>
                          No graded results found.
                        </CustomTableCell>
                      </CustomTableRow>
                    ) : (
                      filteredExams.map((exam) => {
                        const isExamGrading = exam.isGrading || (exam.examType !== 'mcq' && exam.gradingStatus !== 'completed');
                        const scorePercent = exam.percentage !== null && exam.percentage !== undefined ? exam.percentage : (exam.totalScore || 0);
                        const grade = getGradeFromScore(scorePercent);

                        return (
                          <CustomTableRow
                            key={exam.classExamId}
                            hover={!isExamGrading}
                            onClick={() => !isExamGrading && handleRowClick(exam)}
                            sx={{
                              '&:last-child td': { borderBottom: 'none' },
                              cursor: isExamGrading ? 'default' : 'pointer',
                              transition: 'background-color 0.2s',
                              '&:hover': {
                                bgcolor: isExamGrading ? 'transparent !important' : '#fffbf9 !important',
                                '& td': { borderColor: isExamGrading ? '#f1f5f9' : '#ffece8' }
                              }
                            }}
                          >
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', fontFamily: 'Poppins' }}>
                                {stripHtml(exam.examTitle)}
                              </Typography>
                            </CustomTableCell>
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', fontFamily: 'Poppins' }}>
                                {exam.className || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'Poppins', mt: 0.25 }}>
                                {exam.subjectName || 'N/A'}
                              </Typography>
                            </CustomTableCell>
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                              {formatDuration(exam)}
                            </CustomTableCell>
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                              {formatDate(exam.gradedAt)}
                            </CustomTableCell>
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: isExamGrading ? '#64748b' : '#1e293b', fontWeight: isExamGrading ? 500 : 700, fontSize: '13px', fontFamily: 'Poppins' }}>
                              {isExamGrading ? '-' : `${scorePercent}%`}
                            </CustomTableCell>
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                              {isExamGrading ? (
                                <Chip
                                  label="Grading"
                                  size="small"
                                  sx={{
                                    bgcolor: '#FFF7ED',
                                    color: '#D97706',
                                    fontWeight: 700,
                                    fontFamily: 'Poppins',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    height: '22px'
                                  }}
                                />
                              ) : getGradeChip(grade)}
                            </CustomTableCell>
                            <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                              <ExamTypeChip type={exam.examType} />
                            </CustomTableCell>
                            <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                              {!isExamGrading && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick(exam);
                                  }}
                                  sx={{
                                    bgcolor: '#F3F4F6',
                                    color: '#6B7280',
                                    borderRadius: '8px',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: '#E5E7EB' }
                                  }}
                                >
                                  <VisibilityRoundedIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                              )}
                            </CustomTableCell>
                          </CustomTableRow>
                        );
                      })
                    )}
                  </CustomTableBody>
                </CustomTable>
              </CustomTableContainer>

              {/* Table Pagination */}
              {!examsLoading && examsTotalPages > 1 && (
                <CustomPagination
                  currentPage={Math.min(examPage, examsTotalPages)}
                  totalPages={examsTotalPages}
                  onPageChange={(page) => setExamPage(page)}
                />
              )}
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
